import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faInfoCircle,
  faExclamationTriangle,
  faCheckCircle,
  faEye,
  faTrash,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../../common/RefreshButton";
import EmptyStatePanel from "../../common/EmptyStatePanel";
import { getConfidenceDescription } from "../utils/confidenceUtils";
import {
  decodeQuotedPrintable,
  parseZuluTime,
  formatDate,
  formatDateOnly,
  formatTimeOnly,
  parseRiskFactors,
} from "../utils/emailAnalysisUtils";
import EmailAnalysisAPI from "../../../services/EmailAnalysisAPI";

const AnalysisResults = ({
  rawAnalyses,
  selectedAnalysis,
  onAnalysisClick,
  onRefresh,
  userUuid,
  onError,
}) => {
  const [analyses, setAnalyses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Pagination calculations
  const totalPages = Math.ceil(analyses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAnalyses = analyses.slice(startIndex, endIndex);

  // Helper function to determine threat type display based on confidence
  const getThreatTypeDisplay = (scamType, confidence) => {
    if (confidence <= 0.2) {
      return "None";
    }

    return scamType || "UNKNOWN";
  };

  // Data transformation functions
  const transformAnalysisData = (analysisData) => {
    return analysisData.map((item) => {
      console.log("AnalysisResults: Transforming item:", item);
      console.log("AnalysisResults: Item analysisKey:", item.analysisKey);
      console.log(
        "AnalysisResults: Item analysisKey type:",
        typeof item.analysisKey,
      );

      return {
        key: item.analysisKey,
        lastModified: parseZuluTime(item.analysisResult?.analyzedAt),
        size: 0,
        originalKey: item.originalKey,
        originalEmailContent: item.analysisResult?.analyzedEmailContent
          ? item.analysisResult.analyzedEmailContent.body || "No body content"
          : item.originalEmailContent,
        analysis: {
          analysisResult: {
            isScam: item.analysisResult?.isScam || false,
            confidence: item.analysisResult?.confidence || 0,
            scamType: getThreatTypeDisplay(
              item.analysisResult?.scamType,
              item.analysisResult?.confidence || 0,
            ),
            reasoning: item.analysisResult?.explanation || "",
            sender:
              item.analysisResult?.analyzedEmailContent?.senderEmail ||
              item.analysisResult?.sender ||
              "Unknown",
            subject:
              item.analysisResult?.analyzedEmailContent?.subject ||
              item.analysisResult?.subject ||
              "No subject",
            analysisDate: parseZuluTime(item.analysisResult?.analyzedAt),
            suspiciousElements: parseRiskFactors(
              item.analysisResult?.riskFactors,
            ),
            suggestions: item.analysisResult?.suggestions || "",
          },
          analysisTimestamp: parseZuluTime(item.analysisResult?.analyzedAt),
          analysisMethod: "BACKEND_API",
        },
      };
    });
  };

  const sortAnalysesByDate = (data) => {
    return data.sort((a, b) => {
      const getTimestamp = (item) => {
        // Prioritize analysis timestamp from analysisResult.analyzedAt
        const analysisTime =
          item.analysis?.analysisResult?.analysisDate ||
          item.analysis?.analysisTimestamp ||
          item.lastModified;

        if (analysisTime instanceof Date) {
          return analysisTime.getTime();
        }

        // Parse the timestamp and convert to local time
        const parsedTime = parseZuluTime(analysisTime);
        return parsedTime ? parsedTime.getTime() : 0;
      };

      const timeA = getTimestamp(a);
      const timeB = getTimestamp(b);

      // Sort in descending order (latest first)
      return timeB - timeA;
    });
  };

  // Event handlers
  const handleDeleteAnalysis = async (analysisItem) => {
    try {
      await EmailAnalysisAPI.deleteAnalysis(analysisItem.key);
      setAnalyses((prevAnalyses) =>
        prevAnalyses.filter((item) => item.key !== analysisItem.key),
      );
    } catch (err) {
      console.error("AnalysisResults: Error deleting analysis:", err);
      onError("Failed to delete analysis. Please try again.");
    }
  };

  // Pagination functions
  const goToPage = (page) => setCurrentPage(page);
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Effects
  useEffect(() => {
    setCurrentPage(1);
  }, [analyses]);

  useEffect(() => {
    if (rawAnalyses && rawAnalyses.length > 0) {
      const transformedData = transformAnalysisData(rawAnalyses);
      const sortedData = sortAnalysesByDate(transformedData);
      setAnalyses(sortedData.slice(0, 20));
    } else {
      setAnalyses([]);
    }
  }, [rawAnalyses]);
  return (
    <div
      className="common-section analysis-results-section"
      style={{ marginTop: 0, paddingTop: 0 }}
    >
      <div className="common-section-header">
        <div className="section-header-content">
          <div className="section-header-left">
            <h3>
              <FontAwesomeIcon icon={faEnvelope} /> Analysis Results (
              {analyses.length})
            </h3>
            <p>
              Click on any analysis to view detailed results and original email
              content
              {analyses.length > itemsPerPage && (
                <span className="pagination-info">
                  {" "}
                  • Showing {startIndex + 1}-
                  {Math.min(endIndex, analyses.length)} of {analyses.length}
                </span>
              )}
            </p>
          </div>
          <div className="section-header-right">
            <RefreshButton
              onClick={onRefresh}
              title="Refresh analysis results"
            />
          </div>
        </div>
      </div>
      <div className="common-section-content">
        {analyses.length === 0 ? (
          <div className="coming-soon-message">
            <EmptyStatePanel
              icon={faEnvelope}
              title="No Analysis Results Found"
              description="No email analyses match your current search criteria."
            />
            <div className="common-button-group">
              <RefreshButton onClick={onRefresh} />
            </div>
          </div>
        ) : (
          <>
            <div className="analysis-items-container">
              {currentAnalyses.map((item) => (
                <AnalysisItem
                  key={item.key}
                  item={item}
                  isSelected={selectedAnalysis?.key === item.key}
                  onClick={() => onAnalysisClick(item)}
                  onDeleteAnalysis={handleDeleteAnalysis}
                  formatDate={formatDate}
                  formatDateOnly={formatDateOnly}
                  formatTimeOnly={formatTimeOnly}
                  decodeQuotedPrintable={decodeQuotedPrintable}
                />
              ))}
            </div>

            {analyses.length > itemsPerPage && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalItems={analyses.length}
                goToPreviousPage={goToPreviousPage}
                goToNextPage={goToNextPage}
                goToPage={goToPage}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const AnalysisItem = ({
  item,
  isSelected,
  onClick,
  onDeleteAnalysis,
  formatDate,
  formatDateOnly,
  formatTimeOnly,
  decodeQuotedPrintable,
}) => {
  const handleDelete = (e) => {
    e.stopPropagation();
    onDeleteAnalysis(item);
  };

  return (
    <div
      className={`analysis-item-horizontal clickable ${
        isSelected ? "selected" : ""
      } ${item.analysis?.analysisResult?.isScam ? "scam" : ""}`}
      onClick={onClick}
    >
      <div className="analysis-item-content">
        <div
          className={
            item.analysis?.analysisResult?.isScam
              ? "scam-detection-scam status-badge-horizontal status-badge-top-left"
              : "scam-detection-legitimate status-badge-horizontal status-badge-top-left"
          }
        >
          {item.analysis?.analysisResult?.isScam ? (
            <>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="fa-icon-margin-right-4"
              />
              SCAM
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="fa-icon-margin-right-4"
              />
              LEGITIMATE
            </>
          )}
        </div>

        <div className="analysis-item-center">
          <div className="analysis-sender">
            From: {item.analysis?.analysisResult?.sender || "Unknown"}
          </div>
          <div className="analysis-subject">
            {decodeQuotedPrintable(item.analysis?.analysisResult?.subject) ||
              "No subject"}
          </div>
        </div>

        <div className="analysis-item-left">
          <div className="analysis-confidence">
            {getConfidenceDescription(
              item.analysis?.analysisResult?.confidence || 0,
              item.analysis?.analysisResult?.isScam,
            )}
          </div>
          <div className="analysis-date">{formatDate(item.lastModified)}</div>
        </div>

        <div className="analysis-item-right">
          <div className="analysis-item-actions">
            <button
              className="common-button subtle-button"
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              <FontAwesomeIcon
                icon={faEye}
                className="fa-icon-margin-right-4"
              />
              Details
            </button>
            <button
              className="common-button subtle-button"
              onClick={handleDelete}
            >
              <FontAwesomeIcon
                icon={faTrash}
                className="fa-icon-margin-right-4"
              />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Pagination = ({
  currentPage,
  totalPages,
  startIndex,
  endIndex,
  totalItems,
  goToPreviousPage,
  goToNextPage,
  goToPage,
}) => {
  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of{" "}
        {totalItems} results
      </div>
      <div className="pagination-controls">
        <button
          className="common-button small-button"
          onClick={goToPreviousPage}
          disabled={currentPage === 1}
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="fa-icon-margin-right-4"
          />
          Previous
        </button>

        <div className="pagination-pages">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-page ${
                page === currentPage ? "active" : ""
              }`}
              onClick={() => goToPage(page)}
            >
              {page}
            </button>
          ))}
        </div>

        <button
          className="common-button small-button"
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
        >
          Next
          <FontAwesomeIcon
            icon={faChevronRight}
            className="fa-icon-margin-left-4"
          />
        </button>
      </div>
    </div>
  );
};

export default AnalysisResults;
